import { inject, Injectable } from '@angular/core';
import {
  ApiDataAccess,
  HttpApiQueryRepository,
  interpolate,
  Query,
  QueryFilter,
  QueryResult,
} from '@softline/core';
import { QueryDataSource } from '@softapps/core';
import { SOFTLINE_API_ABAU_DOCUMENT_QUERY } from '../invoice-link.api';
import { DeliveryNoteRow, DocumentType, InvoiceRow } from '../types/document-row';

// Both lists show ONLY unlinked documents: the reserved marker `linked`
// (bare name, no table alias) rides on every query — grid blocks, filter option
// lookups and id collections alike.
const ONLY_UNLINKED = { type: 'booleanIsFalse', name: 'linked' } as const;

export abstract class DocumentQueryDataSource<T extends object>
  extends HttpApiQueryRepository<T>
  implements QueryDataSource<T>
{
  type = 'query' as const;

  protected constructor(
    documentType: DocumentType,
    private readonly alias: 'invoice_verification' | 'goods_receipt'
  ) {
    super(
      inject(ApiDataAccess),
      interpolate(SOFTLINE_API_ABAU_DOCUMENT_QUERY, { type: documentType })
    );
  }

  override async load(params: { query: Query<T> }): Promise<QueryResult<T>> {
    return super.load({ query: this.withOnlyUnlinked(params.query) });
  }

  /** Counts the documents a filter matches, in one row. The response's `lastRow` cannot
   *  serve: it comes back as min(total, endRow), so a full window echoes the window. */
  async count(filter: QueryFilter<T> | null): Promise<number | null> {
    // Written out rather than built: the abstract T leaves QueryBuilder unable to
    // resolve the aggregation type for a runtime column path.
    const query = {
      columns: [{ name: `${this.alias}.id`, usage: 'aggregate', agg: 'count' }],
      sorts: [],
      filters: filter ?? null,
      startRow: 0,
      endRow: 1,
      version: 1,
    } as unknown as Query<T>;
    const result = await this.load({ query });
    const row = result.data[0] as Record<string, Record<string, unknown>> | undefined;
    const count = row?.[this.alias]?.['id:count'];
    return typeof count === 'number' ? count : null;
  }

  // The service groups by every requested column, so nothing is added to `columns`:
  // an injected id column would turn the panel's distinct-supplier query into one
  // row per document.
  private withOnlyUnlinked(query: Query<T>): Query<T> {
    const filters = (
      query.filters
        ? { type: 'conditions', operator: 'AND', filters: [ONLY_UNLINKED, query.filters] }
        : ONLY_UNLINKED
    ) as QueryFilter<T>;
    return { ...query, filters };
  }
}

@Injectable()
export class InvoiceQueryRepository extends DocumentQueryDataSource<InvoiceRow> {
  constructor() {
    super('INVOICE_VERIFICATION', 'invoice_verification');
  }
}

@Injectable()
export class DeliveryNoteQueryRepository extends DocumentQueryDataSource<DeliveryNoteRow> {
  constructor() {
    super('PURCHASE_DELIVERY_NOTE', 'goods_receipt');
  }
}
